import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { ApiEnvelope } from './super-admin-auth.service';

export interface ChangePasswordBody {
  old_password: string;
  password: string;
  confirm_password: string;
}

/** Stimulate Agent OCR preference — `null` when unset. */
export type SimulateAgentMethod = 'ai' | 'idp' | null;

export interface AdminProfileData {
  id?: number;
  full_name?: string | null;
  email_id?: string | null;
  country_code?: string | null;
  mobile_number?: string | null;
  simulate_agent_method?: SimulateAgentMethod;
  profile_picture?: {
    preview_url?: string;
    storage_path?: string;
    mime_type?: string;
    original_file_name?: string;
  } | null;
}

export interface UpdateAdminProfileBody {
  full_name?: string;
  email_id?: string;
  country_code?: string;
  mobile_number?: string;
}

@Injectable({ providedIn: 'root' })
export class UserAccountApiService {
  private readonly base = environment.apiBaseUrl.replace(/\/$/, '');

  constructor(private readonly http: HttpClient) {}

  /** Authenticated session cookie or Bearer JWT required. */
  changePassword(body: ChangePasswordBody): Observable<ApiEnvelope<unknown>> {
    return this.http.patch<ApiEnvelope<unknown>>(`${this.base}/user/change-password`, body);
  }

  getAdminProfile(): Observable<ApiEnvelope<AdminProfileData>> {
    return this.http.get<ApiEnvelope<AdminProfileData>>(`${this.base}/user/admin/profile`);
  }

  updateAdminProfile(body: UpdateAdminProfileBody): Observable<ApiEnvelope<AdminProfileData>> {
    return this.http.patch<ApiEnvelope<AdminProfileData>>(`${this.base}/user/admin/profile`, body);
  }

  /** Update logged-in user's Stimulate Agent OCR method (`PATCH /user/admin/simulate-agent-method`). */
  updateSimulateAgentMethod(
    simulate_agent_method: SimulateAgentMethod,
  ): Observable<ApiEnvelope<{ simulate_agent_method: SimulateAgentMethod }>> {
    return this.http.patch<ApiEnvelope<{ simulate_agent_method: SimulateAgentMethod }>>(
      `${this.base}/user/admin/simulate-agent-method`,
      { simulate_agent_method },
    );
  }

  updateAdminProfilePicture(file: File): Observable<ApiEnvelope<AdminProfileData>> {
    const fd = new FormData();
    fd.append('profile_picture', file, file.name);
    return this.http.patch<ApiEnvelope<AdminProfileData>>(`${this.base}/user/admin/profile-picture`, fd);
  }

  /** Removes stored profile picture for the given user id (use authenticated user’s id for “delete my photo”). */
  deleteUserProfilePicture(userId: number): Observable<ApiEnvelope<AdminProfileData>> {
    return this.http.delete<ApiEnvelope<AdminProfileData>>(`${this.base}/user/profile-picture/${userId}`);
  }
}
